import React, { useState, useEffect } from 'react';

const RocketMathRace = () => {
  const [gameState, setGameState] = useState('menu');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [rocketY, setRocketY] = useState(80);
  const [fuel, setFuel] = useState(100);
  const [obstacles, setObstacles] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [altitude, setAltitude] = useState(0);
  const [speed, setSpeed] = useState(2);

  const generateQuestion = () => {
    const types = [
      () => {
        const a = Math.floor(Math.random() * 50) + 20;
        const b = Math.floor(Math.random() * 50) + 20;
        return { q: `${a} + ${b}`, ans: a + b };
      },
      () => {
        const a = Math.floor(Math.random() * 20) + 5;
        const b = Math.floor(Math.random() * 12) + 2;
        return { q: `${a} × ${b}`, ans: a * b };
      },
      () => {
        const b = Math.floor(Math.random() * 10) + 2;
        const ans = Math.floor(Math.random() * 15) + 5;
        return { q: `${b * ans} ÷ ${b}`, ans };
      }
    ];
    
    const gen = types[Math.floor(Math.random() * types.length)];
    const { q, ans } = gen();
    
    const answers = [ans];
    while(answers.length < 3) {
      const wrong = ans + Math.floor(Math.random() * 20) - 10;
      if(wrong > 0 && !answers.includes(wrong)) answers.push(wrong);
    }
    
    return { question: q, answers: answers.sort(() => Math.random() - 0.5), correct: ans };
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setLevel(1);
    setFuel(100);
    setAltitude(0);
    setSpeed(2);
    setRocketY(80);
    setObstacles([]);
    setCurrentQuestion(generateQuestion());
  };

  useEffect(() => {
    if(gameState === 'playing') {
      const interval = setInterval(() => {
        setAltitude(a => a + speed);
        setFuel(f => {
          const newFuel = f - 0.2;
          if(newFuel <= 0) setGameState('gameover');
          return Math.max(0, newFuel);
        });

        setObstacles(prev => {
          const updated = prev.map(o => ({ ...o, y: o.y + speed })).filter(o => o.y < 100);
          
          if(Math.random() < 0.03) {
            const lane = Math.floor(Math.random() * 3);
            updated.push({
              y: -10,
              lane,
              answer: currentQuestion?.answers[Math.floor(Math.random() * 3)],
              id: Date.now() + Math.random()
            });
          }
          
          return updated;
        });

        if(altitude % 1000 === 0 && altitude > 0) {
          setLevel(l => l + 1);
          setSpeed(s => s + 0.5);
        }
      }, 50);

      return () => clearInterval(interval);
    }
  }, [gameState, speed, altitude, currentQuestion]);

  const selectAnswer = (answer) => {
    if(answer === currentQuestion?.correct) {
      setScore(s => s + 100);
      setFuel(f => Math.min(100, f + 15));
      setCurrentQuestion(generateQuestion());
    } else {
      setFuel(f => Math.max(0, f - 20));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 via-purple-900 to-black p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-8 text-yellow-300">
          🚀 Rocket Math Race 🌟
        </h1>

        {gameState === 'menu' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🚀</div>
            <h2 className="text-3xl font-bold mb-4">Blast Off to Space!</h2>
            <p className="text-xl mb-8 text-gray-600">
              Answer math questions to fuel your rocket!
            </p>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              LAUNCH
            </button>
          </div>
        )}

        {gameState === 'playing' && (
          <div>
            <div className="bg-white rounded-lg p-4 mb-4 flex justify-between">
              <div className="font-bold">Score: {score}</div>
              <div className="font-bold">Level: {level}</div>
              <div className="font-bold">Altitude: {Math.floor(altitude)}m</div>
              <div className={`font-bold ${fuel < 30 ? 'text-red-600 animate-pulse' : 'text-green-600'}`}>
                Fuel: {Math.floor(fuel)}%
              </div>
            </div>

            {currentQuestion && (
              <div className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-lg p-6 mb-4 text-center">
                <div className="text-2xl font-bold mb-4">{currentQuestion.question} = ?</div>
                <div className="grid grid-cols-3 gap-4">
                  {currentQuestion.answers.map((ans, i) => (
                    <button
                      key={i}
                      onClick={() => selectAnswer(ans)}
                      className="bg-white text-gray-800 py-3 rounded-lg font-bold text-xl hover:scale-105 transform transition"
                    >
                      {ans}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="relative bg-gradient-to-b from-black to-blue-900 rounded-lg overflow-hidden" style={{ height: '400px' }}>
              {/* Stars */}
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-white rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animation: `twinkle ${Math.random() * 3 + 1}s infinite`
                  }}
                />
              ))}

              {/* Rocket */}
              <div
                className="absolute left-1/2 transform -translate-x-1/2 transition-all duration-300"
                style={{ top: `${rocketY}%` }}
              >
                <div className="text-6xl">🚀</div>
              </div>
            </div>
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🌟</div>
            <h2 className="text-4xl font-bold mb-4">Mission Complete!</h2>
            <div className="text-3xl mb-4 text-blue-600 font-bold">Score: {score}</div>
            <div className="text-2xl mb-8 text-gray-600">Max Altitude: {Math.floor(altitude)}m</div>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              LAUNCH AGAIN
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RocketMathRace;
